import { Link } from 'react-router-dom';
import { useState } from 'react';
import { MenuItem, ListItemIcon, ListItemText, Box, Typography, Divider, Collapse, Skeleton, Tooltip } from '@mui/material';
import {
  Dashboard, People, School, TrendingUp, Gavel, Balance, PersonRemove,
  HealthAndSafety, AttachMoney, VerifiedUser, ChevronLeft, ChevronRight,
  AccountTree, PersonAdd, Event, Assessment, AddCircleOutline, Assignment,
  Folder, List, GroupWork, BarChart, AdminPanelSettings, Storefront,
} from '@mui/icons-material';
import { useEntitlement } from '@/components/domain-engine/useEntitlement';
import { DEFAULT_TENANT_ID } from '@/components/domain-engine/TenantSelector';

interface MenuItemDef {
  label: string;
  icon: React.ReactNode;
  to: string;
  domainId?: string;
  tooltip?: string;
}

interface MenuGroupDef {
  label?: string;
  items: MenuItemDef[];
}

interface MenuSubDomainDef {
  label: string;
  domainId: string;
  groups: MenuGroupDef[];
}

interface MenuDomainDef {
  label: string;
  domainId: string;
  subDomains?: MenuSubDomainDef[];
  groups?: MenuGroupDef[];
}

const domains: MenuDomainDef[] = [
  {
    label: 'Overview',
    domainId: '',
    groups: [
      {
        items: [
          { label: 'Dashboard', icon: <Dashboard />, to: '/', tooltip: 'Platform dashboard overview' },
        ],
      },
    ],
  },
  {
    label: 'HR',
    domainId: 'hr',
    subDomains: [
      {
        label: 'Onboarding',
        domainId: 'hr.onboarding',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Employee Register', icon: <People />, to: '/employees', domainId: 'hr.onboarding', tooltip: 'View and manage all employee records' },
              { label: 'Onboarding Processes', icon: <People />, to: '/onboarding-processes', domainId: 'hr.onboarding', tooltip: 'Track new employee onboarding progress' },
              { label: 'Active Workflows', icon: <AccountTree />, to: '/hr/onboarding/workflows', domainId: 'hr.onboarding', tooltip: 'View employees with active onboarding workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'New Employee', icon: <AddCircleOutline />, to: '/wizard/onboarding', domainId: 'hr.onboarding', tooltip: 'Start the onboarding wizard for a new employee' },
            ],
          },
        ],
      },
      {
        label: 'Skills Development',
        domainId: 'hr.skills-development',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'WSP/ATR Dashboard', icon: <School />, to: '/wsp-atr-dashboard', domainId: 'hr.skills-development', tooltip: 'Workplace Skills Plan and Annual Training Report overview' },
              { label: 'Training Records', icon: <School />, to: '/training-records', domainId: 'hr.skills-development', tooltip: 'Log and track employee training activities' },
              { label: 'Training Providers', icon: <School />, to: '/skills-development/providers', domainId: 'hr.skills-development', tooltip: 'Manage registered training providers and their accreditations' },
              { label: 'Course Catalogue', icon: <School />, to: '/skills-development/courses', domainId: 'hr.skills-development', tooltip: 'Browse and manage available training courses' },
              { label: 'Learnership Contracts', icon: <School />, to: '/contracts', domainId: 'hr.skills-development', tooltip: 'Manage learnership and apprenticeship contracts' },
              { label: 'Active Workflows', icon: <AccountTree />, to: '/hr/skills-development/workflows', domainId: 'hr.skills-development', tooltip: 'View employees with active skills development workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'Log Training', icon: <AddCircleOutline />, to: '/wizard/training', domainId: 'hr.skills-development', tooltip: 'Record a new training event' },
              { label: 'Add Provider', icon: <AddCircleOutline />, to: '/training-providers/create', domainId: 'hr.skills-development', tooltip: 'Register a new training provider' },
              { label: 'Add Course', icon: <AddCircleOutline />, to: '/courses/create', domainId: 'hr.skills-development', tooltip: 'Add a course to the catalogue' },
              { label: 'Submit WSP/ATR', icon: <AddCircleOutline />, to: '/wizard/wsp-atr', domainId: 'hr.skills-development', tooltip: 'Submit the Workplace Skills Plan or Annual Training Report' },
            ],
          },
        ],
      },
      {
        label: 'Industrial Relations',
        domainId: 'hr.industrial-relations',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Disciplinary Cases', icon: <Gavel />, to: '/disciplinary-cases', domainId: 'hr.industrial-relations', tooltip: 'View and manage disciplinary warnings' },
              { label: 'PIPs', icon: <TrendingUp />, to: '/pips', domainId: 'hr.industrial-relations', tooltip: 'Track performance improvement plans' },
              { label: 'Active Workflows', icon: <AccountTree />, to: '/hr/industrial-relations/workflows', domainId: 'hr.industrial-relations', tooltip: 'View employees with active industrial relations workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'New Warning', icon: <AddCircleOutline />, to: '/wizard/disciplinary', domainId: 'hr.industrial-relations', tooltip: 'Issue a new disciplinary warning' },
              { label: 'Open PIP', icon: <AddCircleOutline />, to: '/wizard/pip', domainId: 'hr.industrial-relations', tooltip: 'Create a performance improvement plan' },
            ],
          },
        ],
      },
      {
        label: 'Employment Equity',
        domainId: 'hr.employment-equity',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'EE Profiles', icon: <Balance />, to: '/ee-profiles', domainId: 'hr.employment-equity', tooltip: 'Employment equity profiles for EE reporting' },
              { label: 'Active Workflows', icon: <AccountTree />, to: '/hr/employment-equity/workflows', domainId: 'hr.employment-equity', tooltip: 'View employees with active EE workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'Submit EE Report', icon: <AddCircleOutline />, to: '/wizard/ee-report', domainId: 'hr.employment-equity', tooltip: 'Submit an employment equity report' },
            ],
          },
        ],
      },
      {
        label: 'Offboarding',
        domainId: 'hr.offboarding',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Terminations', icon: <PersonRemove />, to: '/terminations', domainId: 'hr.offboarding', tooltip: 'Manage employee terminations and offboarding' },
              { label: 'Active Workflows', icon: <AccountTree />, to: '/hr/offboarding/workflows', domainId: 'hr.offboarding', tooltip: 'View employees with active offboarding workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'New Termination', icon: <AddCircleOutline />, to: '/wizard/offboarding', domainId: 'hr.offboarding', tooltip: 'Start the offboarding wizard for an employee' },
            ],
          },
        ],
      },
      {
        label: 'OHS',
        domainId: 'hr.ohs',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Incidents', icon: <HealthAndSafety />, to: '/workplace-injuries', domainId: 'hr.ohs', tooltip: 'Record and track workplace injuries' },
              { label: 'Certifications', icon: <HealthAndSafety />, to: '/ohs-certifications', domainId: 'hr.ohs', tooltip: 'OHS certifications and renewal tracking' },
              { label: 'Representatives', icon: <HealthAndSafety />, to: '/ohs-representatives', domainId: 'hr.ohs', tooltip: 'OHS representatives by department' },
              { label: 'Active Workflows', icon: <AccountTree />, to: '/hr/ohs/workflows', domainId: 'hr.ohs', tooltip: 'View employees with active OHS workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'Report Incident', icon: <AddCircleOutline />, to: '/wizard/injury', domainId: 'hr.ohs', tooltip: 'Record a new workplace injury' },
              { label: 'Log Certification', icon: <AddCircleOutline />, to: '/wizard/certification', domainId: 'hr.ohs', tooltip: 'Add an OHS certification record' },
              { label: 'Schedule Event', icon: <AddCircleOutline />, to: '/wizard/ohs-event', domainId: 'hr.ohs', tooltip: 'Schedule an OHS event or inspection' },
            ],
          },
        ],
      },
      {
        label: 'Organization',
        domainId: 'hr.structure',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Departments', icon: <AccountTree />, to: '/organization/departments', domainId: 'hr.structure', tooltip: 'Manage organisational departments' },
              { label: 'Role Templates', icon: <Assignment />, to: '/organization/role-templates', domainId: 'hr.structure', tooltip: 'Define role templates with NQF and contract requirements' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'Add Department', icon: <AddCircleOutline />, to: '/departments/create', domainId: 'hr.structure', tooltip: 'Create a new department' },
              { label: 'Add Role Template', icon: <AddCircleOutline />, to: '/role-templates/create', domainId: 'hr.structure', tooltip: 'Create a new role template' },
            ],
          },
        ],
      },
      {
        label: 'TES & Labour Broker',
        domainId: 'hr.tes-labour-broker',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'TES Dashboard', icon: <GroupWork />, to: '/tes-dashboard', domainId: 'hr.tes-labour-broker', tooltip: 'Overview of temporary employment services' },
              { label: 'Assignments', icon: <GroupWork />, to: '/tes-assignments', domainId: 'hr.tes-labour-broker', tooltip: 'Manage temporary employment assignments' },
              { label: 'Vendors', icon: <GroupWork />, to: '/tes-vendors', domainId: 'hr.tes-labour-broker', tooltip: 'Manage TES vendor relationships' },
              { label: 'Deeming Alerts', icon: <GroupWork />, to: '/deeming-alerts', domainId: 'hr.tes-labour-broker', tooltip: 'Alerts for potential worker deeming' },
              { label: 'Liability Records', icon: <GroupWork />, to: '/liability-records', domainId: 'hr.tes-labour-broker', tooltip: 'Track TES liability obligations' },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Tutoring',
    domainId: 'tutoring',
    subDomains: [
      {
        label: 'Overview',
        domainId: 'tutoring',
        groups: [
          {
            items: [
              { label: 'Tutoring Dashboard', icon: <BarChart />, to: '/tutoring-dashboard', domainId: 'tutoring', tooltip: 'Overview of tutoring operations and metrics' },
            ],
          },
        ],
      },
      {
        label: 'Tutors',
        domainId: 'tutoring.tutor',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Tutors', icon: <People />, to: '/tutors', domainId: 'tutoring.tutor', tooltip: 'View and manage tutor records' },
              { label: 'Qualifications', icon: <School />, to: '/qualifications', domainId: 'tutoring.tutor', tooltip: 'Tutor qualifications verified during onboarding' },
              { label: 'Tutor Workflows', icon: <AccountTree />, to: '/hr/tutoring/tutor/workflows', domainId: 'tutoring.tutor', tooltip: 'View tutors with active workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'Add Tutor', icon: <AddCircleOutline />, to: '/wizard/tutor', domainId: 'tutoring.tutor', tooltip: 'Register a new tutor' },
              { label: 'Add Qualification', icon: <AddCircleOutline />, to: '/wizard/qualification', domainId: 'tutoring.tutor', tooltip: 'Add a qualification to a tutor' },
            ],
          },
        ],
      },
      {
        label: 'Students',
        domainId: 'tutoring.student',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Students', icon: <People />, to: '/students', domainId: 'tutoring.student', tooltip: 'View and manage student records' },
              { label: 'Attendance', icon: <Assessment />, to: '/attendance', domainId: 'tutoring.student', tooltip: 'Track student attendance for tutoring sessions' },
              { label: 'Student Workflows', icon: <AccountTree />, to: '/hr/tutoring/student/workflows', domainId: 'tutoring.student', tooltip: 'View students with active workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'Add Student', icon: <AddCircleOutline />, to: '/wizard/student', domainId: 'tutoring.student', tooltip: 'Register a new student' },
              { label: 'Capture Attendance', icon: <AddCircleOutline />, to: '/wizard/attendance', domainId: 'tutoring.student', tooltip: 'Record attendance for a tutoring session' },
              { label: 'Add Attendance', icon: <AddCircleOutline />, to: '/attendance/create', domainId: 'tutoring.student', tooltip: 'Add a manual attendance record' },
            ],
          },
        ],
      },
      {
        label: 'Clients',
        domainId: 'tutoring.client',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Clients', icon: <People />, to: '/clients', domainId: 'tutoring.client', tooltip: 'Manage client organisations (schools, training centres)' },
              { label: 'Client Workflows', icon: <AccountTree />, to: '/hr/tutoring/client/workflows', domainId: 'tutoring.client', tooltip: 'View clients with active workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'Add Client', icon: <AddCircleOutline />, to: '/wizard/client', domainId: 'tutoring.client', tooltip: 'Register a new client organisation' },
            ],
          },
        ],
      },
      {
        label: 'Services',
        domainId: 'tutoring.service',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Services', icon: <Assignment />, to: '/services', domainId: 'tutoring.service', tooltip: 'View and manage tutoring services' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'Add Service', icon: <AddCircleOutline />, to: '/wizard/service', domainId: 'tutoring.service', tooltip: 'Create a new tutoring service' },
            ],
          },
        ],
      },
      {
        label: 'Qualification Types',
        domainId: 'tutoring.qualification-type',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Qualification Types', icon: <School />, to: '/qualification-types', domainId: 'tutoring.qualification-type', tooltip: 'Manage qualification type definitions' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'Add Qualification Type', icon: <AddCircleOutline />, to: '/wizard/qualification-type', domainId: 'tutoring.qualification-type', tooltip: 'Create a new qualification type' },
            ],
          },
        ],
      },
      {
        label: 'Service Authorisation',
        domainId: 'tutoring.service-authorisation',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Service Authorisations', icon: <Assignment />, to: '/service-authorisations', domainId: 'tutoring.service-authorisation', tooltip: 'View service authorisation records' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'New Service Authorisation', icon: <AddCircleOutline />, to: '/wizard/service-authorisation', domainId: 'tutoring.service-authorisation', tooltip: 'Create a new service authorisation' },
            ],
          },
        ],
      },
      {
        label: 'Agreements',
        domainId: 'tutoring.agreement',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Tutoring Agreements', icon: <Assignment />, to: '/tutoring-agreements', domainId: 'tutoring.agreement', tooltip: 'View and manage tutoring agreements' },
              { label: 'Agreement Workflows', icon: <AccountTree />, to: '/hr/tutoring/agreement/workflows', domainId: 'tutoring.agreement', tooltip: 'View agreements with active workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'New Agreement', icon: <AddCircleOutline />, to: '/wizard/tutoring-agreement', domainId: 'tutoring.agreement', tooltip: 'Create a new tutoring agreement' },
            ],
          },
        ],
      },
      {
        label: 'Schedules',
        domainId: 'tutoring.schedule',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Schedules', icon: <Event />, to: '/tutoring-schedules', domainId: 'tutoring.schedule', tooltip: 'View and manage tutoring schedules' },
              { label: 'Schedule Workflows', icon: <AccountTree />, to: '/hr/tutoring/schedule/workflows', domainId: 'tutoring.schedule', tooltip: 'View schedules with active workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'New Schedule', icon: <AddCircleOutline />, to: '/wizard/tutoring-schedule', domainId: 'tutoring.schedule', tooltip: 'Create a new tutoring schedule' },
            ],
          },
        ],
      },
      {
        label: 'Sessions',
        domainId: 'tutoring.session',
        groups: [
          {
            label: 'Records',
            items: [
              { label: 'Sessions', icon: <Event />, to: '/tutoring-sessions', domainId: 'tutoring.session', tooltip: 'View and manage tutoring sessions' },
              { label: 'Session Workflows', icon: <AccountTree />, to: '/hr/tutoring/session/workflows', domainId: 'tutoring.session', tooltip: 'View sessions with active workflows' },
            ],
          },
          {
            label: 'Actions',
            items: [
              { label: 'Log Session', icon: <AddCircleOutline />, to: '/wizard/tutoring-session', domainId: 'tutoring.session', tooltip: 'Log a new tutoring session' },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Compliance',
    domainId: 'compliance',
    groups: [
      {
        label: 'Records',
        items: [
          { label: 'Audit Logs', icon: <VerifiedUser />, to: '/audit-logs', domainId: 'compliance', tooltip: 'View platform audit trail' },
          { label: 'Compliance Calendar', icon: <VerifiedUser />, to: '/compliance-calendar', domainId: 'compliance', tooltip: 'Track compliance deadlines and submissions' },
          { label: 'DSAR Requests', icon: <VerifiedUser />, to: '/dsar-requests', domainId: 'compliance', tooltip: 'Manage data subject access requests' },
          { label: 'Contract Alerts', icon: <VerifiedUser />, to: '/contract-alerts', domainId: 'compliance', tooltip: 'View contract expiry and renewal alerts' },
          { label: 'Active Workflows', icon: <AccountTree />, to: '/hr/compliance/workflows', domainId: 'compliance', tooltip: 'View compliance items with active workflows' },
        ],
      },
    ],
  },
  {
    label: 'Billing',
    domainId: 'ledger',
    groups: [
      {
        label: 'Records',
        items: [
          { label: 'Billing Dashboard', icon: <AttachMoney />, to: '/ledger-dashboard', domainId: 'ledger', tooltip: 'Overview of billing and financial status' },
          { label: 'Invoices', icon: <Assignment />, to: '/invoices', domainId: 'ledger', tooltip: 'View and manage invoices' },
          { label: 'Receipts', icon: <Assignment />, to: '/receipts', domainId: 'ledger', tooltip: 'View payment receipts' },
          { label: 'Refunds', icon: <TrendingUp />, to: '/refunds', domainId: 'ledger', tooltip: 'View and approve refunds' },
          { label: 'Accounts', icon: <AttachMoney />, to: '/accounts', domainId: 'ledger', tooltip: 'Manage financial accounts' },
          { label: 'Transactions', icon: <TrendingUp />, to: '/transactions', domainId: 'ledger', tooltip: 'Journal transactions — post, void, reverse' },
          { label: 'Journal Entries', icon: <Assignment />, to: '/journal-entries', domainId: 'ledger', tooltip: 'Posted journal entry projections' },
          { label: 'Checkpoints', icon: <AttachMoney />, to: '/account-checkpoints', domainId: 'ledger', tooltip: 'Account balance checkpoints and period snapshots' },
        ],
      },
      {
        label: 'Sponsored Services',
        items: [
          { label: 'Service Catalogue', icon: <Storefront />, to: '/service-catalogues', domainId: 'ledger', tooltip: 'Manage platform service pricing' },
          { label: 'Service Orders', icon: <Storefront />, to: '/service-orders', domainId: 'ledger', tooltip: 'All tenant pay-on-behalf orders' },
        ],
      },
    ],
  },
  {
    label: 'Tasks',
    domainId: '',
    groups: [
      {
        items: [
          { label: 'Task List', icon: <List />, to: '/tasks', tooltip: 'View and manage tasks' },
        ],
      },
    ],
  },
  {
    label: 'Documents',
    domainId: '',
    groups: [
      {
        items: [
          { label: 'File Management', icon: <Folder />, to: '/files', tooltip: 'Browse and manage uploaded files' },
        ],
      },
    ],
  },
  {
    label: 'Platform',
    domainId: '',
    groups: [
      {
        items: [
          { label: 'Tenants', icon: <AccountTree />, to: '/tenants', tooltip: 'Manage tenant organisations' },
          { label: 'Domain Registry', icon: <AccountTree />, to: '/domains', tooltip: 'Manage registered platform domains' },
          { label: 'Entitlement Management', icon: <AdminPanelSettings />, to: '/entitlements', tooltip: 'Manage tenant entitlement allocations' },
        ],
      },
    ],
  },
];

function hasEntitlementOrParent(domainId: string, hasEntitlement: (domainId: string) => boolean): boolean {
  if (hasEntitlement(domainId)) return true;
  const dotIdx = domainId.lastIndexOf('.');
  if (dotIdx > 0) return hasEntitlementOrParent(domainId.substring(0, dotIdx), hasEntitlement);
  return false;
}

function filterGroupsByEntitlement(groups: MenuGroupDef[], hasEntitlement: (domainId: string) => boolean): MenuGroupDef[] {
  return groups
    .map(group => ({
      ...group,
      items: group.items.filter(item => !item.domainId || hasEntitlementOrParent(item.domainId, hasEntitlement)),
    }))
    .filter(group => group.items.length > 0);
}

function filterSubDomainsByEntitlement(subDomains: MenuSubDomainDef[], hasEntitlement: (domainId: string) => boolean): MenuSubDomainDef[] {
  return subDomains
    .map(sub => ({
      ...sub,
      groups: filterGroupsByEntitlement(sub.groups, hasEntitlement),
    }))
    .filter(sub => sub.groups.length > 0);
}

function MenuGroup({ label, items }: MenuGroupDef) {
  return (
    <Box sx={{ mb: 0.5 }} role="group" aria-label={label || undefined}>
      {label && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            color: 'text.disabled',
            px: 1.5,
            py: 0.5,
          }}
        >
          {label}
        </Typography>
      )}
      {items.map(item => (
        <Tooltip key={item.to} title={item.tooltip || item.label} placement="right" arrow>
          <MenuItem
            component={Link}
            to={item.to}
            role="menuitem"
            sx={{ minHeight: 36, borderRadius: 1, mx: 1, '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>
        </Tooltip>
      ))}
    </Box>
  );
}

function MenuSubDomain({ subDomain, depth = 0 }: { subDomain: MenuSubDomainDef; depth?: number }) {
  const [open, setOpen] = useState(false);

  return (
    <Box>
      <Box
        onClick={() => setOpen(prev => !prev)}
        role="button"
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(prev => !prev); } }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pl: 2 + depth * 1.5,
          pr: 2,
          pt: 1,
          pb: 0.25,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
          {subDomain.label}
        </Typography>
        {open ? <ChevronLeft sx={{ fontSize: 14, color: 'text.disabled' }} /> : <ChevronRight sx={{ fontSize: 14, color: 'text.disabled' }} />}
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1, py: 0.25 }}>
          {subDomain.groups.map((group, idx) => (
            <MenuGroup key={`${subDomain.label}-${idx}`} label={group.label} items={group.items} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function MenuDomainSection({ domain, defaultOpen = false }: { domain: MenuDomainDef; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);

  const hasSubDomains = domain.subDomains && domain.subDomains.length > 0;
  const hasGroups = domain.groups && domain.groups.length > 0;

  return (
    <Box>
      <Box
        onClick={() => setOpen(prev => !prev)}
        role="button"
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(prev => !prev); } }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 2,
          pb: 0.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary' }}>
          {domain.label}
        </Typography>
        {open ? <ChevronLeft sx={{ fontSize: 16, color: 'text.disabled' }} /> : <ChevronRight sx={{ fontSize: 16, color: 'text.disabled' }} />}
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ py: 0.25 }}>
          {hasSubDomains && domain.subDomains!.map(sub => (
            <MenuSubDomain key={sub.label} subDomain={sub} />
          ))}
          {hasGroups && domain.groups!.map((group, idx) => (
            <MenuGroup key={`${domain.label}-${idx}`} label={group.label} items={group.items} />
          ))}
        </Box>
      </Collapse>
      <Divider sx={{ mx: 2 }} />
    </Box>
  );
}

export const RostaMenu = () => {
  const { hasEntitlement, isLoading } = useEntitlement(DEFAULT_TENANT_ID);

  const filteredDomains = domains
    .filter(domain => {
      if (!domain.domainId) return true;
      return hasEntitlement(domain.domainId);
    })
    .map(domain => ({
      ...domain,
      groups: domain.groups ? filterGroupsByEntitlement(domain.groups, hasEntitlement) : undefined,
      subDomains: domain.subDomains ? filterSubDomainsByEntitlement(domain.subDomains, hasEntitlement) : undefined,
    }))
    .filter(domain => {
      if (!domain.domainId) return true;
      if (domain.subDomains && domain.subDomains.length > 0) return true;
      if (domain.groups && domain.groups.length > 0) return true;
      return false;
    });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} role="navigation" aria-label="Main navigation">
        <Box sx={{ flex: 1, overflow: 'auto', py: 1 }} role="menubar">
          {Array.from({ length: 8 }).map((_, i) => (
            <Box key={i} sx={{ px: 2, py: 1.5 }}>
              <Skeleton variant="text" width={120} height={14} />
              <Skeleton variant="text" width={180} height={12} sx={{ mt: 0.5 }} />
              <Skeleton variant="text" width={140} height={12} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} role="navigation" aria-label="Main navigation">
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }} role="menubar">
        {filteredDomains.map((domain, i) => (
          <MenuDomainSection key={domain.label} domain={domain} defaultOpen={i === 0 || i === 1} />
        ))}
      </Box>
    </Box>
  );
};
